import { useTranslation } from "react-i18next";
import { X, Trash } from "lucide-react";
import ReactMarkdown from "react-markdown";
import {
  getGetUserSummarizersQueryKey,
  useDeleteSummarizer,
  useGetUserSummarizers,
} from "@packages/api/madlenAPI";
import { useEffect, useState } from "react";
import { useToast } from "@packages/contexts/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import Joyride, { CallBackProps, Step } from "react-joyride";
import { joyrideStyles, joyrideStylesDark } from "@packages/utils";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { useMobile } from "@packages/hooks/useMobile";
import { trackEvent } from "@packages/hooks/useAnalytics";
import SummaryCard from "../../components/summaries/SummaryCard";
import HeroTitle from "../../components/tool_components/HeroTitle";
import ImageOrVideo from "../../components/tool_components/ImageOrVideo";
import DeleteConfirmPopUp from "../../components/tool_components/DeleteConfirmPopUp";
import ErrorComponent from "../../components/tool_components/ErrorComponent";
import { Button, Text } from "@core/index";
import remarkGfm from "remark-gfm";
import ToolHelper from "@features/tools/components/ToolHelper";
import { useTheme } from "@packages/hooks/useTheme";

const SummariesView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [runTour, setRunTour] = useState(false);
  const [skipAllTours] = useLocalStorage("skip-all-tours", false);
  const isMobile = useMobile();
  const [selectedSummary, setSelectedSummary] = useState<any | null>(null);
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const { theme } = useTheme();
  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="flex flex-col items-center gap-4">
          <video
            src="/tutorials/summaries.mp4"
            autoPlay
            loop
            muted
            className="w-[350px] rounded-xl"
          />
          <Text variant={"p"} className="text-center">
            onboarding.summaries.tour.welcome
          </Text>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".create-button",
      content: t("onboarding.summaries.tour.create"),
      placement: "bottom-end",
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;

    if (action === "close") {
      setRunTour(false);
      return;
    }

    if (status === "finished" || status === "skipped") {
      setRunTour(false);
      if (status === "finished") {
        navigate("/content/summaries/create");
      }
    }
  };

  const { addToast } = useToast();
  const {
    data: summaries,
    isLoading,
    error,
    refetch,
  } = useGetUserSummarizers();
  console.log(summaries);
  const queryClient = useQueryClient();
  const queryKey = getGetUserSummarizersQueryKey();
  const { mutate: deleteSummarizer } = useDeleteSummarizer({
    mutation: {
      onMutate: async ({ id }) => {
        const previousData = queryClient.getQueryData(queryKey);
        // Optimistically update UI
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData?.data?.filter((summary: any) => summary.id !== id),
          };
        });

        return { previousData };
      },
      onError: (error, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
        console.error("Failed to delete connection:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });
  const handleDelete = async (summaryId: number) => {
    try {
      await deleteSummarizer({ id: summaryId });
      addToast({
        title: t("summaries.deleted"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      addToast({
        title: t("summaries.deleteError"),
        description: t("summaries.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  useEffect(() => {
    const shouldShowTour =
      !isLoading &&
      (!summaries?.data || summaries.data.length === 0) &&
      !skipAllTours &&
      !isMobile;
    setRunTour(shouldShowTour);
  }, [isLoading, summaries?.data, skipAllTours, isMobile]);

  const renderModal = () =>
    selectedSummary && (
      <div className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="p-3 flex justify-end items-center">
            <Button
              size="icon"
              rounded="full"
              onClick={() => setSelectedSummary(null)}
              bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
              className="w-10 h-10"
            >
              <X size={24} className="text-gray-500" />
            </Button>
          </div>

          <div className="flex-1 overflow-auto px-6 pb-6">
            <SummaryCard
              createdAt={selectedSummary.createdAt ?? ""}
              content={selectedSummary.markdownSummary}
              imageUrl={selectedSummary.imageUrl}
              videoUrl={selectedSummary.videoUrl}
              author={selectedSummary.authorName ?? ""}
              cefrLevel={selectedSummary.cefrLevel}
              contentLanguage={selectedSummary.contentLanguage}
              onCreateAgain={() => {}}
              title={selectedSummary.title}
            />
          </div>
        </div>
      </div>
    );
  const LoadingState = () => (
    <div
      className="flex flex-col items-center justify-center min-h-[400px] space-y-4"
      data-testid="loading-dot-container"
    >
      <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      <Text variant={"p"} status="subtle">
        summaries.loading
      </Text>
    </div>
  );

  const { trackEvent: track } = trackEvent();
  const handleClick = () => {
    track(
      {
        category: "ContentGeneration",
        action: "summary.generate",
        label: "Summary generation click",
        nonInteraction: false,
      },
      {
        location: "/summary/generate",
        element: "generate-button",
      },
    );
  };

  const textToDelete =
    summaries?.data
      ?.find((text) => text.id === showDeletePopup)
      ?.markdownSummary?.substring(0, 30)
      ?.replaceAll("*", "")
      ?.replaceAll("\n", "")
      ?.replaceAll("#", "") || t("textLeveler.untitledText");

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-teal-100 dark:from-gray-900 dark:to-teal-900">
      <Joyride
        locale={{
          back: t("onboarding.back"),
          close: t("onboarding.close"),
          last: t("onboarding.finish"),
          next: t("onboarding.next"),
          skip: t("onboarding.skip"),
        }}
        steps={steps}
        run={!skipAllTours && !isMobile && runTour}
        continuous
        showSkipButton
        styles={theme === "dark" ? joyrideStylesDark : joyrideStyles}
        callback={handleJoyrideCallback}
      />
      <div className="container max-w-7xl mx-auto px-4 py-4">
        {/* Hero Section */}
        <HeroTitle
          onClick={handleClick}
          title={t("quickActions.summaries")}
          description={t("quickActions.summariesDesc")}
          titleStyle="bg-gradient-to-r from-teal-600 to-emerald-600 dark:from-teal-400 dark:to-emerald-400"
          titleBackgroundStyle="bg-gradient-to-r from-teal-50/30 to-emerald-50/30 dark:from-teal-900/30 dark:to-emerald-900/30"
          buttonStyle="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 create-button"
          buttonLink="/content/summaries/create"
        />

        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorComponent
            title={t("summaries.error.title")}
            refetch={refetch}
            description={t("summaries.error.description")}
            retryButton={t("summaries.error.retryButton")}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {summaries?.data
              ?.filter((summary) => summary.markdownSummary)
              .map((summary) => (
                <div
                  key={summary.id}
                  className="group cursor-pointer bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 hover:bg-gradient-to-br hover:from-white/90 hover:to-teal-50/90 dark:hover:from-gray-800/90 dark:hover:to-teal-900/90"
                >
                  <div className="p-6 h-full relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      rounded="xl"
                      className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 shadow-lg hover:shadow-xl scale-90 group-hover:scale-100"
                      bgColor="bg-white/90 dark:bg-gray-900/90 hover:bg-red-100 dark:hover:bg-red-900/50"
                      borderColor="border-gray-200/50 dark:border-gray-700/50"
                      leftIcon={
                        <Trash
                          size={16}
                          className="text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
                        />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeletePopup(summary.id);
                      }}
                    />

                    <div
                      onClick={() => setSelectedSummary(summary)}
                      className="h-96"
                    >
                      <div className="relative object-cover h-48 mx-auto rounded-2xl shadow-lg group-hover:shadow-xl flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 mb-4 overflow-hidden transition-all duration-300 group-hover:scale-[1.02]">
                        <ImageOrVideo
                          imageUrl={summary.imageUrl}
                          videoUrl={summary.videoUrl}
                          isLoading={false}
                          resolution="high"
                          createdAt={summary.createdAt}
                        />
                        {/* CEFR Level Chip - Only show for English content */}
                        {summary.cefrLevel &&
                          ["en", "fr", "de", "es"].includes(i18n.language) && (
                            <div className="absolute top-3 right-3 px-2.5 py-1 bg-teal-600 text-white text-xs font-medium rounded-full shadow-lg border border-teal-700 z-10">
                              {summary.cefrLevel}
                            </div>
                          )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>

                      <div
                        className={`prose dark:prose-invert relative overflow-hidden max-w-none ${summary.imageUrl ? "h-[calc(100%-12rem)]" : "h-full"}
                        before:absolute before:inset-0 before:bg-gradient-to-t before:from-white before:to-transparent before:dark:from-gray-800/50 before:dark:to-transparent before:z-10 before:pointer-events-none
                        `}
                      >
                        {summary.markdownSummary && (
                          <div className="text-gray-600 dark:text-gray-300 line-clamp-6 overflow-hidden text-ellipsis prose-headings:mt-0 prose-p:mt-2 prose-p:mb-0 transition-colors group-hover:text-gray-900 dark:group-hover:text-gray-100">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              className="prose dark:prose-invert max-w-none bg-white/50 dark:bg-gray-800/50 rounded-xl 
              prose-table:border-collapse prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 
              prose-td:p-2 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:p-2
              prose-table:w-full prose-table:my-4 prose-pre:whitespace-pre-wrap prose-pre:break-words  prose-pre:overflow-auto prose-pre:max-w-full"
                            >
                              {summary.markdownSummary}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Modal */}
        {renderModal()}

        <ToolHelper
          toolPath={"summaries"}
          shouldShowTour={
            !skipAllTours &&
            !isMobile &&
            summaries?.data &&
            summaries?.data?.length === 0
          }
          toolType={t(`sidebar.menu.toolbox.summaries`)}
        />

        {/* Delete Confirmation PopUp */}
        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("summaries.confirmDelete", {
              title: textToDelete + "...",
            })}
            showDeletePopup={showDeletePopup}
            setShowDeletePopup={setShowDeletePopup}
            handleDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
};

export default SummariesView;
